import { useState } from 'react';
import { useToast } from '../../hooks/useToast';
import { useBusy } from '../../hooks/useBusy';
import { useAfterWrite } from '../../hooks/useAfterWrite';
import { leaveRefereePool, removeRefereeFromPool } from '../../api/client';
import { noviceLabel } from '../../utils/officialNovice';
import { useI18n, formatDay } from '../../i18n';
import { useAuthStore } from '../../store/auth';
import Avatar from '../../components/shared/Avatar';
import EmptyState from '../../components/shared/EmptyState';
import ConfirmModal, { SECOND_QUESTION_ARM_MS } from '../../components/shared/ConfirmModal';
import PingModal from '../../components/shared/PingModal';
import RefereeModal from '../../components/tournament/RefereeModal';
import OfficialsSummaryModal from '../../components/tournament/OfficialsSummaryModal';
import PushOffHint from '../../components/PushOffHint';
import { PencilIcon, BellIcon, CheckIcon, ClipboardIcon, PeopleIcon, ChartIcon } from '../../components/shared/Icons';
import { personName } from '../../utils/personName';
import { ALL_OFFICIAL_STATS_ROLES } from '../../utils/officialStats';
import { boutScoreState } from '../../utils/tournamentState';
import type { ApiWarning, Tournament, TournamentReferee, Registration, Bout } from '../../types';
import { BADGE_XXS } from '../../components/shared/badgeSize';

interface OfficialsTabProps {
  id: string;
  tournament: Tournament | null;
  referees: TournamentReferee[];
  registrations: Registration[];
  managesThisTournament: boolean;
  canOfficiate: boolean;
  isInRefereePool: boolean;
  tournamentDays: string[];
  isMultiDay: boolean;
  bouts: Bout[];
  reload: () => Promise<void>;
  setShowOfficialsPlan: (show: boolean) => void;
  setShowOfficialsView: (show: boolean) => void;
}

export default function OfficialsTab({
  id, tournament, referees, registrations,
  managesThisTournament, canOfficiate, isInRefereePool,
  tournamentDays, isMultiDay, bouts, reload, setShowOfficialsPlan, setShowOfficialsView,
}: OfficialsTabProps) {
  const { showError } = useToast();
  const afterWrite = useAfterWrite();
  // #605 — one leave at a time: a second tap sends no second request.
  const leaving = useBusy();
  // #605 — one removal at a time (#589: it was outside run()).
  const removing = useBusy();
  const { t } = useI18n();
  const { user, hasRole } = useAuthStore();
  const canSeeTeam = managesThisTournament || hasRole('admin', 'jury');
  // #588 — the officials of the bouts: the page already shows these data.
  const canViewOfficials = managesThisTournament || isInRefereePool || hasRole('admin', 'jury');
  // #616 — the load of every official of this tournament: the managers of the
  // tournament (they hold the tournament_manager role), the jury role and an
  // admin. A referee sees only the own numbers (operator, 2026-10-17).
  const canSeeAllOfficialStats = hasRole(...ALL_OFFICIAL_STATS_ROLES);
  const [showSummary, setShowSummary] = useState(false);
  const allScored = boutScoreState(bouts).all;

  const [showRefJoinModal, setShowRefJoinModal] = useState(false);
  const [showRefAddModal, setShowRefAddModal] = useState(false);
  const [editingRefId, setEditingRefId] = useState<string | null>(null);
  // #589 — the shared confirm modal replaces confirm() and prompt().
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [pingTarget, setPingTarget] = useState<TournamentReferee | null>(null);

  async function handleLeaveRefereePool() {
    await leaving.run(async () => {
      // The question is answered: the modal closes, and the Leave button
      // stays off until the answer of the server.
      setConfirmLeave(false);
      let advice: ApiWarning[] | undefined;
      try {
        advice = (await leaveRefereePool(id)).data?.warnings;
      } catch (err) { showError(err); return; }
      // #419 — the mutation committed. A failed refetch is a different fact
      // and must not read as a failed leave. #589 (D1) — the answer names the
      // pending bouts that lost an official of the pool: the advice replaces
      // the success text, and a failed refetch keeps it (#664).
      await afterWrite({
        source: 'OfficialsTab:leave',
        success: advice?.length ? undefined : t.tournament.leftPool,
        warnings: advice,
        refresh: reload,   // #412 — sequenced
      });
    });
  }

  async function handleRemoveReferee(userId: string) {
    await removing.run(async () => {
      setConfirmRemoveId(null);
      let advice: ApiWarning[] | undefined;
      try {
        advice = (await removeRefereeFromPool(id, userId)).data?.warnings;
      } catch (err) { showError(err); return; }
      await afterWrite({
        source: 'OfficialsTab:remove',
        success: advice?.length ? undefined : t.tournament.refereeRemoved,
        warnings: advice,
        refresh: reload,   // #412 — sequenced
      });
    });
  }

  return (
    <div className="space-y-4">
      {/* #557 — an official of this tournament with no push subscription on
          this device: the call to the ring and the seat list push. The
          PushPromptBanner shows to the athletes and the coaches only (#495),
          so this line is the opt-in of an official. Not after the last bout. */}
      {isInRefereePool && !allScored && <PushOffHint text={t.notifications.pushOffHintOfficial} />}
      {/* #552 — empty:hidden: a viewer with no pool action gets no empty row. */}
      <div className="flex gap-2 flex-wrap items-center empty:hidden">
        {canOfficiate && !isInRefereePool && !allScored && (
          <button onClick={() => setShowRefJoinModal(true)} className="btn-primary btn-sm">{t.tournament.refereePoolJoinBtn}</button>
        )}
        {showRefJoinModal && (
          <RefereeModal mode="join" tournamentId={id} tournamentDays={tournamentDays} isMultiDay={isMultiDay}
            onSaved={async () => { await reload(); }}
            onClose={() => setShowRefJoinModal(false)} />
        )}
        {isInRefereePool && (
          <>
            <span className="badge badge-green text-xs inline-flex items-center gap-1"><CheckIcon className="w-3 h-3" /> {t.tournament.refereePoolInBadge}</span>
            <button onClick={() => setConfirmLeave(true)} disabled={leaving.busy} className="btn-outline-red btn-sm" data-testid="leave-pool-btn">{t.tournament.refereePoolLeaveBtn}</button>
          </>
        )}
        {managesThisTournament && !allScored && <>
          <button onClick={() => setShowRefAddModal(true)} className="btn-outline btn-sm" data-testid="add-referee-btn">+ {t.refereeModal.addTitle}</button>
          {showRefAddModal && (
            <RefereeModal mode="add" tournamentId={id} tournamentDays={tournamentDays} isMultiDay={isMultiDay}
              existingIds={referees.map(r => r.id)}
              onSaved={async () => { await reload(); }}
              onClose={() => setShowRefAddModal(false)} />
          )}
        </>}
        {canViewOfficials && bouts.length > 0 && (
          <button onClick={() => setShowOfficialsView(true)} data-testid="view-officials-btn"
            className="btn-outline btn-sm inline-flex items-center gap-1"><PeopleIcon className="w-3.5 h-3.5" /> {t.viewOfficials.title}</button>
        )}
        {managesThisTournament && referees.length >= 3 && bouts.length > 0 && !allScored && (
          <button onClick={() => setShowOfficialsPlan(true)} className="btn-outline btn-sm inline-flex items-center gap-1"><ClipboardIcon className="w-3.5 h-3.5" /> {t.planOfficials.title}</button>
        )}
        {canSeeAllOfficialStats && bouts.length > 0 && (
          <button onClick={() => setShowSummary(true)} data-testid="officials-summary-btn"
            className="btn-outline btn-sm inline-flex items-center gap-1"><ChartIcon className="w-3.5 h-3.5" /> {t.officials.summary}</button>
        )}
        {showSummary && <OfficialsSummaryModal tournamentId={id} onClose={() => setShowSummary(false)} />}
      </div>

      <div className="space-y-2">
        {referees.length === 0 && <EmptyState compact testId="pool-empty" title={t.tournament.noRefereesInPool} />}
        {referees.map(ref => (
          <div key={ref.id} className="card p-4 flex items-center gap-3" data-testid={`pool-row-${ref.id}`}>
            <Avatar firstName={ref.first_name} lastName={ref.last_name} photoUrl={ref.photo_url} size="md" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                {personName(ref.first_name, ref.last_name)}
                {ref.in_jury_pool && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200"><ClipboardIcon className="w-3 h-3" /> {t.roles.jury}</span>
                )}
                {ref.team_name && canSeeTeam && (
                  <span className="text-xs text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded-full border border-gray-200">{ref.team_name}</span>
                )}
                {/* #589, #642 — the computed novice status of each seat, only for the callers who see the team. */}
                {noviceLabel(ref, t) && (
                  <span className="text-xs font-semibold text-sky-700 bg-sky-50 px-2 py-0.5 rounded-full border border-sky-200" data-testid={`pool-novice-${ref.id}`}>{noviceLabel(ref, t)}</span>
                )}
              </div>
              {managesThisTournament && <div className="text-xs text-gray-500">{ref.email}</div>}
              {isMultiDay && (
                <div className="flex flex-wrap gap-0.5 mt-0.5">
                  {ref.available_dates ? (ref.available_dates as string[]).map(d => (
                    <span key={d} className={`badge badge-gray ${BADGE_XXS} px-1`}>{formatDay(d, 'EEE d')}</span>
                  )) : <span className="text-gray-400">{t.tournament.allDays}</span>}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {ref.id === user?.id && <span className="badge badge-green text-xs">{t.common.you}</span>}
              {managesThisTournament && ref.id !== user?.id && !allScored && (
                <button onClick={() => setPingTarget(ref)}
                  className="text-gray-300 hover:text-amber-500 transition-colors p-1" data-testid={`pool-ping-${ref.id}`}
                  title={t.tournament.notifyPoolMember.replace('{name}', personName(ref.first_name, ref.last_name))}
                  aria-label={t.tournament.notifyPoolMember.replace('{name}', personName(ref.first_name, ref.last_name))}>
                  <BellIcon className="w-5 h-5" filled />
                </button>
              )}
              {(managesThisTournament || ref.id === user?.id) && !allScored && (
                <button onClick={() => setEditingRefId(ref.id)} className="btn-icon" title={t.common.edit} aria-label={t.common.edit} data-testid={`pool-edit-${ref.id}`}>
                  <PencilIcon className="w-4 h-4" />
                </button>
              )}
              {editingRefId === ref.id && (
                <RefereeModal mode="edit" tournamentId={id} ref_={ref} tournamentDays={tournamentDays} isMultiDay={isMultiDay}
                        onSaved={async () => { await reload(); }}
                  onRemove={managesThisTournament ? () => setConfirmRemoveId(ref.id) : undefined}
                  onClose={() => setEditingRefId(null)} />
              )}
            </div>
          </div>
        ))}
      </div>
      {confirmLeave && (
        <ConfirmModal destructive title={t.tournament.leavePoolTitle} message={t.tournament.leavePoolConfirm}
          confirmLabel={t.common.leave} busy={leaving.busy} testId="leave-pool-confirm"
          onConfirm={handleLeaveRefereePool} onCancel={() => setConfirmLeave(false)} />
      )}
      {/* The second question of a removal: RefereeModal asked the first one.
          It arms after a pause: a double-click on the first question must not answer it (#661, bug 81). */}
      {confirmRemoveId && (
        <ConfirmModal destructive title={t.tournament.removeRefereeTitle} message={t.tournament.removeRefereeConfirm}
          confirmLabel={t.common.remove} busy={removing.busy} armMs={SECOND_QUESTION_ARM_MS} testId="remove-referee-confirm"
          onConfirm={() => handleRemoveReferee(confirmRemoveId)} onCancel={() => setConfirmRemoveId(null)} />
      )}
      {pingTarget && (
        <PingModal target={pingTarget} sendTestId="pool-ping-send"
          // #557 review — the role of the sender, in the language of the sender, as the ping of the jury.
          // An admin is not always a manager of the tournament.
          title={hasRole('admin') ? t.roles.admin : t.roles.tournament_manager}
          defaultText={t.tournament.pingTournament.replace('{tournament}', tournament?.name || t.tournament.fallbackName)}
          url={`/tournaments/${id}`} tournamentId={id} onClose={() => setPingTarget(null)} />
      )}
    </div>
  );
}
